import { useState } from 'react';
import vendorApplicationService from '../api/services/vendorApplicationService';

const useVendorApplications = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearError = () => setError(null);

  const getAllApplications = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendorApplicationService.getAllApplications();
      if (response.success) {
        return response.data;
      } else {
        setError(response.message || 'Failed to fetch applications. Please try again.');
        return null;
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Network error. Please check your connection and try again.');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getApplicationById = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendorApplicationService.getApplicationById(id);
      if (response.success) {
        return response.data;
      } else {
        setError(response.message || 'Failed to fetch application details.');
        return null;
      }
    } catch (err) {
      console.error('Error fetching application:', err);
      setError('Network error. Please check your connection and try again.');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const approveApplication = async (id, adminNotes = '') => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendorApplicationService.approveApplication(id, adminNotes);
      if (response.success) {
        return true;
      } else {
        // Provide more specific error messages
        let errorMessage = response.message || 'Failed to approve application.';
        
        // Check for specific error types
        if (response.message?.includes('already approved')) {
          errorMessage = 'This application has already been approved.';
        } else if (response.message?.includes('not found')) {
          errorMessage = 'Application not found. It may have been deleted.';
        } else if (response.message?.includes('email')) {
          errorMessage = 'Failed to send email notification. Please try again.';
        } else if (response.message?.includes('password')) {
          errorMessage = 'Failed to generate vendor account. Please try again.';
        }
        
        setError(errorMessage);
        return false;
      }
    } catch (err) {
      console.error('Error approving application:', err);
      setError('Network error. Please check your connection and try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const rejectApplication = async (id, adminNotes = '') => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendorApplicationService.rejectApplication(id, adminNotes);
      if (response.success) {
        return true;
      } else {
        // Provide more specific error messages
        let errorMessage = response.message || 'Failed to reject application.';
        
        // Check for specific error types
        if (response.message?.includes('already rejected')) {
          errorMessage = 'This application has already been rejected.';
        } else if (response.message?.includes('not found')) {
          errorMessage = 'Application not found. It may have been deleted.';
        } else if (response.message?.includes('email')) {
          errorMessage = 'Failed to send email notification. Please try again.';
        }
        
        setError(errorMessage);
        return false;
      }
    } catch (err) {
      console.error('Error rejecting application:', err);
      setError('Network error. Please check your connection and try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteApplication = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendorApplicationService.deleteApplication(id);
      if (response.success) {
        return true;
      } else {
        let errorMessage = response.message || 'Failed to delete application.';
        
        if (response.message?.includes('not found')) {
          errorMessage = 'Application not found. It may have already been deleted.';
        }
        
        setError(errorMessage);
        return false;
      }
    } catch (err) {
      console.error('Error deleting application:', err);
      setError('Network error. Please check your connection and try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };



  return {
    loading,
    error,
    getAllApplications,
    getApplicationById,
    approveApplication,
    rejectApplication,
    deleteApplication,
    clearError
  };
};

export default useVendorApplications; 